import PropTypes from "prop-types";
import { useEffect, useRef, useState, useMemo } from "react";
import * as d3 from "d3";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import Loader from "../Loader";
import NoData from "../Nodata";
import { eventsToTrackSegments, formatDuration } from "./utils";
import { escapeHtml } from "../../utils/apiValidation";
import { generateChartTicks } from "../../utils/utility";
import {
  EVENT_COLORS,
  DEFAULT_SEGMENT_COLOR,
  MARGIN,
  TIMELINE_BAR_HEIGHT,
  TIMELINE_BAR_RADIUS,
  TRACK_GAP,
  LABEL_CHAR_WIDTH,
  ABBREV_MIN_WIDTH,
  X_AXIS_GAP,
  X_AXIS_EXTRA_HEIGHT,
  NOW_INDICATOR_THRESHOLD_MS,
  TIMELINE_TRACKS,
} from "./constants";
import "./StateTimeline.scss";

dayjs.extend(utc);
dayjs.extend(tz);

function StateTimeline({ events, loading, dateRange, siteTimezone, onZoomChange, zoomExtremes }) {
  const wrapperRef = useRef(null);
  const svgContainerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const xScaleRef = useRef(null);
  const updateChartRef = useRef(null);
  const currentDomainRef = useRef(null);
  const resetZoomRef = useRef(null);

  // Clear persisted zoom domain when dateRange changes (e.g. 7 days → Today)
  useEffect(() => {
    currentDomainRef.current = null;
  }, [dateRange]);

  // Observe container resizes
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Build per-track segments and determine which tracks to show
  const trackData = useMemo(() => {
    if (!events || events.length === 0 || !dateRange) return [];
    const [start, end] = dateRange;
    const startMs =
      start instanceof Date ? start.getTime() : typeof start === "number" ? start : new Date(start).getTime();
    const endMs = end instanceof Date ? end.getTime() : typeof end === "number" ? end : new Date(end).getTime();
    const range = [startMs, endMs];

    return TIMELINE_TRACKS.map(track => {
      const segments = eventsToTrackSegments(events, range, track);
      return { track, segments };
    }).filter(({ track, segments }) => track.alwaysShow || segments.length > 0);
  }, [events, dateRange]);

  // Collect unique event names across all visible tracks for legend
  const activeLabels = useMemo(() => {
    const seen = new Set();
    const result = [];
    trackData.forEach(({ segments }) => {
      segments.forEach(s => {
        if (!seen.has(s.label)) {
          seen.add(s.label);
          result.push(s.label);
        }
      });
    });
    return result;
  }, [trackData]);

  // ── D3 Render ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (trackData.length === 0 || !svgContainerRef.current || !dateRange) return;

    const container = svgContainerRef.current;
    const totalWidth = container.clientWidth;
    if (totalWidth <= 0) return;

    const numTracks = trackData.length;
    const barH = TIMELINE_BAR_HEIGHT;
    const totalBarHeight = numTracks * barH + (numTracks - 1) * TRACK_GAP;
    const totalHeight = totalBarHeight + MARGIN.top + MARGIN.bottom + X_AXIS_EXTRA_HEIGHT;

    const width = totalWidth - MARGIN.left - MARGIN.right;

    // Clear previous render
    d3.select(container).selectAll("*").remove();

    const svg = d3.select(container).append("svg").attr("width", totalWidth).attr("height", totalHeight);

    const g = svg.append("g").attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

    // Time scale - use dateRange to align with other charts
    const [start, end] = dateRange;
    const startMs =
      start instanceof Date ? start.getTime() : typeof start === "number" ? start : new Date(start).getTime();
    const endMs = end instanceof Date ? end.getTime() : typeof end === "number" ? end : new Date(end).getTime();
    const originalDomainMs = [startMs, endMs];

    const xScale = d3
      .scaleTime()
      .domain([new Date(startMs), new Date(endMs)])
      .range([0, width]);

    xScaleRef.current = xScale;

    // X axis — timezone-aware tick generation
    const { tickValues: initialTickValues, tickFormat: initialTickFormat } = generateChartTicks(
      startMs,
      endMs,
      siteTimezone
    );

    g.append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${totalBarHeight + X_AXIS_GAP})`)
      .call(
        d3.axisBottom(xScale).tickValues(initialTickValues).tickSize(0).tickPadding(8).tickFormat(initialTickFormat)
      )
      .select(".domain")
      .remove();

    // Tooltip
    const tooltipDiv = d3.select(wrapperRef.current).append("div").attr("class", "state-timeline-tooltip");

    const formatDate = ts => {
      const d = siteTimezone ? dayjs(ts).tz(siteTimezone) : dayjs(ts);
      return d.format("D MMM YYYY");
    };

    const formatTime = ts => {
      const d = siteTimezone ? dayjs(ts).tz(siteTimezone) : dayjs(ts);
      return d.format("HH:mm:ss");
    };

    const positionTooltip = (mouseX, mouseY) => {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const svgRect = svgContainerRef.current.getBoundingClientRect();
      const relLeft = svgRect.left - wrapperRect.left;
      const relTop = svgRect.top - wrapperRect.top;
      const tipW = tooltipDiv.node().offsetWidth;
      const anchorX = relLeft + MARGIN.left + mouseX;
      const fitsRight = anchorX + 12 + tipW < wrapperRect.width;
      const left = fitsRight ? anchorX + 12 : anchorX - tipW - 24;
      tooltipDiv.style("left", `${left}px`).style("top", `${relTop + MARGIN.top + mouseY - 10}px`);
    };

    const getSegmentTooltipHtml = (seg, trackLabel) =>
      `<div style="margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid rgba(255,255,255,0.1);">` +
      `<div style="font-size: 14px; font-weight: 600;">${escapeHtml(formatDate(seg.start))}</div>` +
      `<div style="font-size: 12px; opacity: 0.8;">${escapeHtml(formatTime(seg.start))}</div>` +
      `</div>` +
      `<div style="font-size: 10px; opacity: 0.6; margin-bottom: 2px;">${escapeHtml(trackLabel)}</div>` +
      `<div style="margin-bottom: 4px;"><strong>${escapeHtml(seg.label)}</strong></div>` +
      `<div style="font-size: 12px; opacity: 0.9;">` +
      `${escapeHtml(formatTime(seg.start))} → ${escapeHtml(formatTime(seg.end))}<br/>` +
      `Duration: <strong>${escapeHtml(formatDuration(seg.durationMs))}</strong>` +
      `</div>`;

    const getNoEventTooltipHtml = (timestamp, trackLabel) =>
      `<div style="margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid rgba(255,255,255,0.1);">` +
      `<div style="font-size: 14px; font-weight: 600;">${escapeHtml(formatDate(timestamp))}</div>` +
      `<div style="font-size: 12px; opacity: 0.8;">${escapeHtml(formatTime(timestamp))}</div>` +
      `</div>` +
      `<div style="font-size: 10px; opacity: 0.6; margin-bottom: 2px;">${escapeHtml(trackLabel)}</div>` +
      `<div style="font-size: 12px; opacity: 0.7; font-style: italic;">No event</div>`;

    // Helper: returns true if a hex color is light (needs dark text)
    const isLightColor = hex => {
      const c = hex.replace("#", "");
      const r = parseInt(c.substring(0, 2), 16);
      const gVal = parseInt(c.substring(2, 4), 16);
      const b = parseInt(c.substring(4, 6), 16);
      return (r * 299 + gVal * 587 + b * 114) / 1000 > 180;
    };

    const defs = svg.append("defs");

    // Store per-track bar groups and segment data for zoom updates
    const trackBarGroups = [];

    // Draw each track
    trackData.forEach(({ track, segments }, trackIdx) => {
      const trackY = trackIdx * (barH + TRACK_GAP);

      // Track label (left of the bar)
      g.append("text")
        .attr("class", "track-label")
        .attr("x", -8)
        .attr("y", trackY + barH / 2)
        .attr("text-anchor", "end")
        .attr("dominant-baseline", "middle")
        .attr("fill", "#64748b")
        .attr("font-size", "11px")
        .attr("font-weight", "500")
        .text(track.label);

      // Clip path per track for rounded corners
      const clipId = `timeline-track-clip-${track.id}`;
      defs
        .append("clipPath")
        .attr("id", clipId)
        .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", width)
        .attr("height", barH)
        .attr("rx", TIMELINE_BAR_RADIUS);

      const barGroup = g.append("g").attr("transform", `translate(0,${trackY})`).attr("clip-path", `url(#${clipId})`);

      trackBarGroups.push({ barGroup, segments, track });

      // Background for empty track
      barGroup
        .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", width)
        .attr("height", barH)
        .attr("fill", "#f1f5f9")
        .attr("class", "track-bg");

      // Draw segments
      if (segments.length > 0) {
        segments.forEach(seg => {
          const x1 = xScale(new Date(seg.start));
          const x2 = xScale(new Date(seg.end));
          const segWidth = Math.max(x2 - x1, 1);
          const segColor = EVENT_COLORS[seg.label] || DEFAULT_SEGMENT_COLOR;
          const textColor = isLightColor(segColor) ? "#334155" : "#fff";

          barGroup
            .append("rect")
            .datum(seg)
            .attr("x", x1)
            .attr("y", 0)
            .attr("width", segWidth)
            .attr("height", barH)
            .attr("fill", segColor)
            .attr("class", "state-segment-rect");

          // Label inside segment (only if wide enough)
          const labelText = `${seg.label} (${formatDuration(seg.durationMs)})`;
          const minLabelWidth = labelText.length * LABEL_CHAR_WIDTH;

          if (segWidth > minLabelWidth) {
            barGroup
              .append("text")
              .datum(seg)
              .attr("class", "state-segment-label")
              .attr("x", x1 + segWidth / 2)
              .attr("y", barH / 2 + 1)
              .attr("text-anchor", "middle")
              .attr("dominant-baseline", "middle")
              .attr("fill", textColor)
              .text(labelText);
          } else if (segWidth > ABBREV_MIN_WIDTH) {
            const short = seg.label
              .split(" ")
              .map(w => w[0])
              .join("");
            barGroup
              .append("text")
              .datum(seg)
              .attr("class", "state-segment-label state-segment-label--abbrev")
              .attr("x", x1 + segWidth / 2)
              .attr("y", barH / 2 + 1)
              .attr("text-anchor", "middle")
              .attr("dominant-baseline", "middle")
              .attr("fill", textColor)
              .text(short);
          }
        });
      }
    });

    // Chart update helper (called by zoom & pan)
    const updateChart = newDomainMs => {
      xScale.domain([new Date(newDomainMs[0]), new Date(newDomainMs[1])]);
      const { tickValues: zoomTickValues, tickFormat: zoomTickFormat } = generateChartTicks(
        newDomainMs[0],
        newDomainMs[1],
        siteTimezone
      );
      g.select(".x-axis").call(
        d3.axisBottom(xScale).tickValues(zoomTickValues).tickSize(0).tickPadding(8).tickFormat(zoomTickFormat)
      );
      g.select(".x-axis .domain").remove();

      // Redraw segments in all tracks
      trackBarGroups.forEach(({ barGroup }) => {
        barGroup
          .selectAll(".state-segment-rect")
          .attr("x", d => xScale(new Date(d.start)))
          .attr("width", d => {
            const x1 = xScale(new Date(d.start));
            const x2 = xScale(new Date(d.end));
            return Math.max(x2 - x1, 1);
          });

        barGroup.selectAll(".state-segment-label").attr("x", d => {
          const x1 = xScale(new Date(d.start));
          const x2 = xScale(new Date(d.end));
          return x1 + Math.max(x2 - x1, 1) / 2;
        });
      });

      currentDomainRef.current = newDomainMs;
    };

    updateChartRef.current = updateChart;

    if (currentDomainRef.current) {
      updateChart(currentDomainRef.current);
    }

    const resetZoom = () => {
      currentDomainRef.current = null;
      updateChart(originalDomainMs);
      if (onZoomChange) {
        onZoomChange(null);
      }
    };
    resetZoomRef.current = resetZoom;

    // Brush for zoom — spans the full track area
    let isPanning = false;
    let panStartClientX = 0;
    let panStartDomainMs = [...originalDomainMs];

    const brush = d3
      .brushX()
      .extent([
        [0, 0],
        [width, totalBarHeight],
      ])
      .filter(event => !event.shiftKey && !event.button)
      .on("end", function (event) {
        if (!event.selection) return;
        const [x0, x1] = event.selection;
        let tMin = xScale.invert(x0).getTime();
        let tMax = xScale.invert(x1).getTime();

        const MIN_ZOOM_MS = 5000;
        if (tMax - tMin < MIN_ZOOM_MS) {
          const mid = (tMin + tMax) / 2;
          tMin = mid - MIN_ZOOM_MS / 2;
          tMax = mid + MIN_ZOOM_MS / 2;
        }

        if (tMin < originalDomainMs[0]) tMin = originalDomainMs[0];
        if (tMax > originalDomainMs[1]) tMax = originalDomainMs[1];

        brushG.call(brush.clear);
        updateChart([tMin, tMax]);

        if (onZoomChange) {
          onZoomChange({ min: tMin, max: tMax, source: "timeline" });
        }
      });

    const brushG = g.append("g").attr("class", "brush").call(brush);

    // Tooltip on hover — determine which track based on mouse Y
    const bisect = d3.bisector(d => d.start).left;

    const showBrushTooltip = event => {
      const [mouseX, mouseY] = d3.pointer(event);
      const xVal = xScale.invert(mouseX).getTime();

      // Determine which track the mouse is over
      const trackIdx = Math.floor(mouseY / (barH + TRACK_GAP));
      const clamped = Math.min(Math.max(trackIdx, 0), trackData.length - 1);
      const { track, segments: trackSegments } = trackData[clamped];

      // Find segment at this timestamp in this track
      const idx = bisect(trackSegments, xVal);
      const activeSegment =
        idx > 0 && trackSegments[idx - 1].end >= xVal
          ? trackSegments[idx - 1]
          : idx < trackSegments.length && trackSegments[idx].start <= xVal && trackSegments[idx].end >= xVal
            ? trackSegments[idx]
            : null;

      if (!activeSegment) {
        tooltipDiv.html(getNoEventTooltipHtml(xVal, track.label)).style("display", "block");
      } else {
        tooltipDiv.html(getSegmentTooltipHtml(activeSegment, track.label)).style("display", "block");
      }

      positionTooltip(mouseX, mouseY);
    };

    brushG
      .select(".overlay")
      .on("mousemove.tooltip", showBrushTooltip)
      .on("mouseleave.tooltip", () => tooltipDiv.style("display", "none"));

    // Shift+drag panning
    brushG.on("mousedown.pan", function (event) {
      if (!event.shiftKey) return;
      isPanning = true;
      panStartClientX = event.clientX;
      panStartDomainMs = xScale.domain().map(d => d.getTime());
    });

    const handleMouseMovePan = event => {
      if (!isPanning) return;
      const dx = event.clientX - panStartClientX;
      const domainWidth = panStartDomainMs[1] - panStartDomainMs[0];
      const dxTime = domainWidth * (dx / width);

      let newMin = panStartDomainMs[0] - dxTime;
      let newMax = panStartDomainMs[1] - dxTime;

      if (newMin < originalDomainMs[0]) {
        newMin = originalDomainMs[0];
        newMax = newMin + domainWidth;
      }
      if (newMax > originalDomainMs[1]) {
        newMax = originalDomainMs[1];
        newMin = newMax - domainWidth;
      }

      updateChart([newMin, newMax]);

      if (onZoomChange) {
        onZoomChange({ min: newMin, max: newMax, source: "timeline" });
      }
    };

    const handleMouseUpPan = () => {
      isPanning = false;
    };

    window.addEventListener("mousemove", handleMouseMovePan);
    window.addEventListener("mouseup", handleMouseUpPan);

    // Double-click resets zoom
    brushG.on("dblclick.reset", resetZoom);

    // "now" indicator
    const now = Date.now();
    if (now >= startMs && now <= endMs + NOW_INDICATOR_THRESHOLD_MS) {
      g.append("text")
        .attr("class", "state-timeline-now")
        .attr("x", width + 4)
        .attr("y", totalBarHeight + 18)
        .text("› now");
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMovePan);
      window.removeEventListener("mouseup", handleMouseUpPan);
      tooltipDiv.remove();
      d3.select(container).selectAll("*").remove();
      resetZoomRef.current = null;
    };
  }, [trackData, containerSize, siteTimezone, onZoomChange, dateRange]);

  // Sync zoom from external source (other charts)
  useEffect(() => {
    if (zoomExtremes === null) {
      if (resetZoomRef.current) {
        resetZoomRef.current();
      }
      return;
    }

    if (!zoomExtremes || zoomExtremes.source === "timeline" || !updateChartRef.current) return;

    updateChartRef.current([zoomExtremes.min, zoomExtremes.max]);
  }, [zoomExtremes]);

  const hasData = trackData.length > 0 && trackData.some(({ segments }) => segments.length > 0);

  return (
    <div ref={wrapperRef} className="state-timeline-wrapper">
      {/* Header */}
      <div className="state-timeline-header">
        <svg
          className="state-timeline-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
        </svg>
        <span className="state-timeline-title">State Timeline</span>
      </div>

      {/* Chart area */}
      <div className="state-timeline-chart">
        {!loading && (!events || events.length === 0) ? (
          <NoData />
        ) : loading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center state-timeline-loading">
            <Loader />
          </div>
        ) : null}

        <div ref={svgContainerRef} className="w-full" />
      </div>

      {/* Legend */}
      {hasData && (
        <div className="state-timeline-legend">
          {activeLabels.map(label => (
            <div key={label} className="state-timeline-legend__item">
              <div
                className="state-timeline-legend__dot"
                style={{ backgroundColor: EVENT_COLORS[label] || DEFAULT_SEGMENT_COLOR }}
              />
              {label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

StateTimeline.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      eventName: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
    })
  ),
  loading: PropTypes.bool.isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.string])),
  siteTimezone: PropTypes.string,
  onZoomChange: PropTypes.func,
  zoomExtremes: PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    source: PropTypes.string.isRequired,
  }),
};

StateTimeline.defaultProps = {
  events: [],
  dateRange: undefined,
  siteTimezone: "UTC",
  onZoomChange: null,
  zoomExtremes: null,
};

export default StateTimeline;
